import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { MAX_DELIVERY_LINES } from '@interfaces/ILogistics';
import { useComboDismiss } from '@hooks/useComboDismiss';
import { ChevronDownIcon } from '@utils/icons';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { PickCombo } from '@components/ui/PickCombo/PickCombo';
import type { PickPrefill } from '../orderPrefill';
import { MAX_QUANTITY, quantityIssue, unitChoices, unitKeyOf, unitLabel } from '@d-orca/shared/client/logistics';
import { CloseMatchChip, FieldNotice, SimilarNameNudge } from './SimilarNameNudge';
import { UnitChoiceNote } from './UnitChoiceNote';
import { rankNameOptions } from './similarMaterials';
import { makeSeedHelpers } from './orderFormSeeds';
import type { UnsyncedOrderNotice } from './oneOpenOrder';
import {
  blankDraft,
  duplicateIndexOf,
  type IDeliveryLineDraft,
  type StockUnitOf,
} from './deliveryLines';
import styles from './DeliveryLinesField.module.css';

export interface IDeliveryLinesFieldProps {
  /** The form's `useId` — row control ids hang off it. */
  fieldId: string;
  lines: IDeliveryLineDraft[];
  onChange: (next: IDeliveryLineDraft[]) => void;
  /** materialSuggestions from the site-logistics payload. */
  suggestions: string[];
  /** Fills a picked material's habitual fields — see `buildPickPrefill`. The
   *  prefill is kept ON the row it filled (`IDeliveryLineDraft.prefill`), which
   *  is what lets the form re-derive the order's suggestions from the SET of
   *  materials rather than from whichever one happened to be picked first. */
  materialPrefill?: (name: string) => PickPrefill;
  /** One-unsynced-line rule, per row: the order already holding this material
   *  open, or null. */
  openOrderNotice?: (name: string) => UnsyncedOrderNotice | null;
  onViewOrder?: (ref: string) => void;
  /** The unit each material is counted in on site: a counted material's
   *  line uses it, locked, on a create and an edit alike (QA 2026-10-18). */
  stockUnitOf: StockUnitOf;
  /** "Count it in another unit" — see `UnitChoiceNote`. */
  onCountInOtherUnit?: (material: string) => void;
  /** Editing an existing order: a picked material's HABITUAL unit does not
   *  lock (only a counted material's stock unit does). */
  isEdit: boolean;
  /** The amount the SYSTEM worked out for each row, by draft key
   *  (`SeededFields.lines`) — per row, because a pick seeds every row it fills
   *  and not only the first. */
  seededQuantities: Record<string, string>;
  /** "Enough for N days at R/day" under a seeded amount — asked per row, since
   *  each material's figure is explained by its own level. */
  quantityHint: (line: IDeliveryLineDraft) => ReactNode;
}

/**
 * The order dialog's materials table (design 2026-09-14 §4.1): one row per
 * material — the existing material search, its amount, its unit, and a remove
 * — with "+ Add material" under them.
 *
 * Extracted from `DeliveryOrderForm` rather than grown inside it: the form was
 * already at the 600-line cap, and every rule that is now PER LINE (the
 * one-open notice, the unit lock, the similar-name nudge, the duplicate guard)
 * lives here in one place instead of being threaded through the order fields.
 */
export function DeliveryLinesField({
  fieldId,
  lines,
  onChange,
  suggestions,
  materialPrefill,
  stockUnitOf,
  onCountInOtherUnit,
  openOrderNotice,
  onViewOrder,
  isEdit,
  seededQuantities,
  quantityHint,
}: IDeliveryLinesFieldProps) {
  const { t } = useTranslation();
  /** Which row's material field was last LEFT — only that row may show the
   *  similar-name nudge, since a nudge mid-typing is flicker and two at once
   *  is noise. */
  const [leftKey, setLeftKey] = useState<string | null>(null);
  /** Material inputs by row key, so picking a material already on the order
   *  can put the caret on the row that has it instead of adding a second. */
  const inputs = useRef(new Map<string, HTMLInputElement | null>());

  const patch = (index: number, next: Partial<IDeliveryLineDraft>) => {
    onChange(lines.map((line, i) => (i === index ? { ...line, ...next } : line)));
  };

  const pick = (index: number, name: string) => {
    const duplicate = duplicateIndexOf(lines, name, index);
    if (duplicate !== -1) {
      inputs.current.get(lines[duplicate]!.key)?.focus();
      return;
    }
    const p = materialPrefill?.(name);
    const stockUnit = stockUnitOf(name);
    patch(index, {
      itemName: name,
      unitWas: undefined,
      ...(stockUnit != null ? { unit: stockUnit, unitLocked: true } : {}),
      ...(p
        ? {
            /* Kept on the row: the order's destination, ETA and note are facts
               about the SET of materials, and the form re-derives all three
               from these the moment a row is added or removed. */
            prefill: p,
            quantity: p.quantity != null ? String(p.quantity) : '',
            unit: stockUnit ?? p.unit ?? '',
            // Lock to a known unit: always to a counted material's stock unit,
            // and to a habitual one only on a create.
            unitLocked: stockUnit != null || (!isEdit && p.unit != null && p.unit !== ''),
          }
        : {}),
    });
  };

  return (
    <div className={styles.field} data-tour="logistics-order-materials">
      <span className={styles.label}>{t('logistics.orderDialog.materials')}</span>
      <div className={styles.rows}>
        {lines.map((line, index) =>
          line.synced ? (
            <SyncedLineRow key={line.key} line={line} index={index} fieldId={fieldId} />
          ) : (
            <DeliveryLineRow
              key={line.key}
              line={line}
              index={index}
              fieldId={fieldId}
              suggestions={suggestions}
              openOrderNotice={openOrderNotice}
              onViewOrder={onViewOrder}
              removable={lines.length > 1}
              /* The later of two rows naming one material — the row the save
                 is refused over, so the reason sits on it. */
              duplicate={duplicateIndexOf(lines.slice(0, index), line.itemName, -1) !== -1}
              nudgeVisible={leftKey === line.key}
              onLeave={() => setLeftKey(line.key)}
              onTypeName={(value) => {
                setLeftKey(null);
                /* Typing diverges from any picked material — drop the prefill:
                 the amount, the amber mark and the order-level suggestions it
                 fed were all claims about a material this row no longer names.
                 The unit locks to a counted material's stock unit when the
                 typed name is one, exactly as a pick would, and is released
                 otherwise. */
                const stockUnit = stockUnitOf(value.trim());
                patch(index, {
                  itemName: value,
                  prefill: null,
                  unitWas: undefined,
                  ...(stockUnit != null ? { unit: stockUnit, unitLocked: true } : { unitLocked: false }),
                });
              }}
              onPick={(name) => pick(index, name)}
              // Retyping the amount is the re-check a switched unit asked for.
              onQuantity={(value) => patch(index, { quantity: value, unitWas: undefined })}
              onUnit={(value) => patch(index, { unit: value })}
              onRemove={() => onChange(lines.filter((_, i) => i !== index))}
              registerInput={(el) => inputs.current.set(line.key, el)}
              seededQuantity={seededQuantities[line.key]}
              quantityHint={quantityHint(line)}
              stockUnit={line.unitLocked ? stockUnitOf(line.itemName.trim()) : null}
              onCountInOtherUnit={onCountInOtherUnit}
              t={t}
            />
          ),
        )}
      </div>
      {/* The truck's own bound. A twenty-first material is another order, not
          a longer form — the server refuses it either way. */}
      {lines.length < MAX_DELIVERY_LINES ? (
        <button
          type="button"
          className={styles.addBtn}
          onClick={() => onChange([...lines, blankDraft()])}
        >
          {t('logistics.orderDialog.addMaterial')}
        </button>
      ) : null}
    </div>
  );
}

interface ISyncedLineRowProps {
  line: IDeliveryLineDraft;
  index: number;
  fieldId: string;
}

/** A line whose material is already synced into stock. It is history — the
 *  arrival its stock was reconciled from — so material, amount and unit read
 *  as locked values, and there is no remove. */
function SyncedLineRow({ line, index, fieldId }: ISyncedLineRowProps) {
  const { t } = useTranslation();
  const locked = `${styles.input} ${styles.lockedValue}`;
  return (
    <div className={styles.lineBlock} data-line-row={index}>
      <div className={styles.row}>
        <span id={`${fieldId}-item-${index}`} className={locked}>
          {line.itemName}
        </span>
        <span className={`${locked} ${styles.amount}`}>{line.quantity}</span>
        <span data-testid={`order-unit-${index}`} className={locked}>
          {unitLabel(t, line.unit)}
        </span>
      </div>
    </div>
  );
}

interface IDeliveryLineRowProps {
  line: IDeliveryLineDraft;
  index: number;
  fieldId: string;
  suggestions: string[];
  openOrderNotice?: (name: string) => UnsyncedOrderNotice | null;
  onViewOrder?: (ref: string) => void;
  /** The last row cannot be removed — an order with no material is a delete,
   *  and the server refuses the empty array. */
  removable: boolean;
  /** Another row above already names this material. */
  duplicate: boolean;
  nudgeVisible: boolean;
  onLeave: () => void;
  onTypeName: (value: string) => void;
  onPick: (name: string) => void;
  onQuantity: (value: string) => void;
  onUnit: (value: string) => void;
  onRemove: () => void;
  registerInput: (el: HTMLInputElement | null) => void;
  /** The amount the system worked out for THIS row, or undefined when it
   *  worked out none. */
  seededQuantity: string | undefined;
  quantityHint: ReactNode;
  /** The stock unit this row's unit is locked to, or null when it is not a
   *  counted material's lock. */
  stockUnit: string | null;
  onCountInOtherUnit?: (material: string) => void;
  t: TFunction;
}

/** One material's row. Owns its own combo dismissal — a hook cannot be called
 *  per item from the list above. */
function DeliveryLineRow({
  line,
  index,
  fieldId,
  suggestions,
  openOrderNotice,
  onViewOrder,
  removable,
  duplicate,
  nudgeVisible,
  onLeave,
  onTypeName,
  onPick,
  onQuantity,
  onUnit,
  onRemove,
  registerInput,
  seededQuantity,
  quantityHint,
  stockUnit,
  onCountInOtherUnit,
  t,
}: IDeliveryLineRowProps) {
  const comboRef = useRef<HTMLDivElement | null>(null);
  const combo = useComboDismiss(comboRef);
  /* Built per ROW, and with THIS module's styles rather than the form's: the
     amber rule is `.input.prefilled`, and each CSS module hashes its own
     `.input`. A plain factory, not a hook — it can be called from the row. */
  const { seededCls, seedNote } = makeSeedHelpers({ quantity: seededQuantity }, t, styles);
  const query = line.itemName.trim();
  const ranked = rankNameOptions(query, suggestions);
  const conflict = openOrderNotice?.(query) ?? null;
  // The caret promises a list to open — keyed off the SOURCE suggestions so a
  // site with names keeps a stable affordance instead of one that vanishes
  // mid-typing whenever the filter happens to miss.
  const isCombo = suggestions.length > 0;
  const amber = seededCls('quantity', line.quantity);
  const issue = quantityIssue(line.quantity);

  return (
    <div className={styles.lineBlock} data-line-row={index}>
      <div className={styles.row}>
        {/* Each row repeats the same three fields, so the column names are
            said once for assistive tech and drawn as placeholders for
            everyone else — a visible label per cell would treble the form's
            height for three words. */}
        <label htmlFor={`${fieldId}-item-${index}`} className="visually-hidden">
          {t('logistics.orderDialog.item')}
        </label>
        <div
          ref={comboRef}
          className={styles.combo}
          onBlur={(e) => {
            // Keyboard path (Tab away); pointer paths are the hook's job.
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) combo.setOpen(false);
          }}
        >
          <input
            id={`${fieldId}-item-${index}`}
            className={isCombo ? `${styles.input} ${styles.comboInput}` : styles.input}
            value={line.itemName}
            onChange={(e) => {
              onTypeName(e.target.value);
              combo.setOpen(true);
            }}
            onBlur={onLeave}
            onClick={combo.tryOpen}
            ref={registerInput}
            maxLength={120}
            placeholder={t('logistics.orderDialog.itemExample')}
            autoComplete="off"
          />
          {isCombo ? (
            <button
              type="button"
              data-testid={`item-caret-${index}`}
              className={styles.caretBtn}
              tabIndex={-1}
              aria-hidden="true"
              onClick={combo.toggle}
            >
              <ChevronDownIcon size={12} />
            </button>
          ) : null}
          {combo.open && ranked.options.length > 0 ? (
            <div className={styles.comboList} role="listbox" onMouseDown={(e) => e.preventDefault()}>
              <ScrollArea>
                {ranked.options.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={ranked.closeMatches.has(s) ? `${styles.comboOpt} ${styles.comboOptStrong}` : styles.comboOpt}
                    onClick={() => {
                      combo.setOpen(false);
                      onPick(s);
                    }}
                  >
                    {s}
                    <CloseMatchChip show={ranked.closeMatches.has(s)} t={t} />
                  </button>
                ))}
              </ScrollArea>
            </div>
          ) : null}
        </div>
        <label htmlFor={`${fieldId}-quantity-${index}`} className="visually-hidden">
          {t('logistics.orderDialog.quantity')}
        </label>
        <input
          id={`${fieldId}-quantity-${index}`}
          className={`${styles.input} ${styles.amount}${amber}${line.unitWas ? ` ${styles.prefilled}` : ''}`}
          value={line.quantity}
          onChange={(e) => onQuantity(e.target.value)}
          inputMode="decimal"
          placeholder={t('logistics.orderDialog.quantity')}
          autoComplete="off"
        />
        <label
          id={`${fieldId}-unit-label-${index}`}
          htmlFor={`${fieldId}-unit-${index}`}
          className="visually-hidden"
        >
          {t('logistics.orderDialog.unit')}
        </label>
        {line.unitLocked ? (
          /* Picked an existing material: its unit is a fact of that material's
             deliveries, shown read-only. Editing the name unlocks it. */
          <span
            id={`${fieldId}-unit-${index}`}
            data-testid={`order-unit-${index}`}
            aria-labelledby={`${fieldId}-unit-label-${index}`}
            className={`${styles.input} ${styles.lockedValue}`}
          >
            {unitLabel(t, line.unit)}
          </span>
        ) : (
          /* The KEY is what the line stores, the label what the viewer reads;
             a legacy stored label resolves to its key so it shows translated.
             Clearing is not offered: every line needs an amount and a unit, so
             there is nothing valid to clear TO — a wrong unit is replaced. */
          <PickCombo
            id={`${fieldId}-unit-${index}`}
            testId={`order-unit-${index}`}
            value={unitKeyOf(line.unit) ?? line.unit}
            options={unitChoices(t)}
            placeholder={t('logistics.orderDialog.unit')}
            onPick={onUnit}
          />
        )}
        {removable ? (
          <button
            type="button"
            className={styles.removeBtn}
            aria-label={t('logistics.orderDialog.removeMaterial', { name: line.itemName })}
            onClick={onRemove}
          >
            ×
          </button>
        ) : (
          /* The last row keeps the column so the grid does not shift — there
             is simply nothing to press. */
          <span className={styles.removeSpacer} aria-hidden="true" />
        )}
      </div>
      {issue ? (
        <span className={styles.fieldWarn} role="alert">
          {issue === 'too-large'
            ? t('logistics.orderDialog.quantityTooLarge', { max: MAX_QUANTITY.toLocaleString('en-US') })
            : t('logistics.orderDialog.quantityInvalid')}
        </span>
      ) : null}
      {duplicate ? (
        <span className={styles.fieldWarn} role="alert">
          {t('logistics.orderDialog.duplicateMaterial')}
        </span>
      ) : null}
      {amber !== '' ? quantityHint : null}
      {seedNote('quantity', line.quantity, true)}
      {stockUnit != null ? (
        <UnitChoiceNote
          material={line.itemName.trim()}
          stockUnit={stockUnit}
          unitWas={line.unitWas}
          quantity={line.quantity}
          onCountInOtherUnit={onCountInOtherUnit}
          t={t}
        />
      ) : null}
      <FieldNotice
        tone="error"
        visible={conflict != null && !combo.open}
        message={t(conflict?.messageKey ?? 'logistics.oneOpen.order', {
          ref: conflict?.ref ?? '',
          name: conflict?.itemName ?? '',
        })}
        actionLabel={t('logistics.oneOpen.view', { ref: conflict?.ref ?? '' })}
        onAction={() => conflict != null && onViewOrder?.(conflict.ref)}
      />
      <SimilarNameNudge
        closeMatch={conflict != null ? null : ranked.closeMatch}
        visible={nudgeVisible}
        message={t('logistics.similarName.nudgeDelivery', { name: ranked.closeMatch ?? '' })}
        onUse={onPick}
        t={t}
      />
    </div>
  );
}
